/**
 * Trainova AI — Multi-Locale Dictionary Index & Registry
 */

import { en } from './en';
import { enGB } from './en-GB';
import { es } from './es';
import { hi } from './hi';

// High-fidelity localized definitions for additional enterprise languages
const fr: Record<string, string> = {
  ...en,
  'nav.dashboard': 'Tableau de bord',
  'nav.simulations': 'Simulations',
  'nav.scenarios': 'Scénarios',
  'nav.courses': 'Cours',
  'nav.my_attempts': 'Mes Tentatives',
  'nav.assessments': 'Évaluations',
  'nav.reports': 'Rapports',
  'nav.settings': 'Paramètres',
  'nav.admin_console': 'Console d\'administration',
  'nav.group_workspace': 'Espace de travail',
  'nav.group_administration': 'Administration',
  'nav.sign_out': 'Déconnexion',
  'nav.language': 'Langue',
  'auth.sign_in': 'Se connecter',
  'auth.welcome_back': 'Bon retour',
  'dashboard.greeting': 'Bon retour, {{name}}',
  'dashboard.assigned_simulations': 'Simulations Assignées',
  'dashboard.start_simulation': 'Démarrer la simulation',
  'briefing.title': 'Briefing du Scénario',
  'briefing.start_simulation_btn': 'Entrer dans la salle de simulation',
  'simulation.finish_session': 'Terminer la session',
  'simulation.retry': 'Réessayer la simulation',
  'scorecard.title': 'Fiche d\'évaluation des compétences',
  'scorecard.overall_score': 'Score global de performance',
  'breadcrumb.home': 'Accueil',
  'breadcrumb.admin': 'Administration',
  'breadcrumb.learner_portal': 'Portail apprenant',
  'breadcrumb.administration_console': 'Console d\'administration',
  'breadcrumb.scenarios': 'Scénarios',
  'breadcrumb.my_attempts': 'Mes tentatives',
  'table.simulation': 'Simulation',
  'table.category': 'Catégorie',
  'table.status': 'Statut',
  'table.actions': 'Actions',
  'table.member': 'Membre',
  'table.duration': 'Durée',
  'table.score': 'Score',
  'table.view_details': 'Voir les détails',
  'table.filter_all': 'Tous',
  'table.sort_latest': 'Trier: Plus récent',
  'common.save': 'Enregistrer',
  'common.cancel': 'Annuler',
  'common.close': 'Fermer',
};

const de: Record<string, string> = {
  ...en,
  'nav.dashboard': 'Dashboard',
  'nav.simulations': 'Simulationen',
  'nav.scenarios': 'Szenarien',
  'nav.courses': 'Kurse',
  'nav.my_attempts': 'Meine Versuche',
  'nav.assessments': 'Bewertungen',
  'nav.reports': 'Berichte',
  'nav.settings': 'Einstellungen',
  'nav.admin_console': 'Verwaltungskonsole',
  'nav.group_workspace': 'Arbeitsbereich',
  'nav.group_administration': 'Verwaltung',
  'nav.sign_out': 'Abmelden',
  'nav.language': 'Sprache',
  'auth.sign_in': 'Anmelden',
  'auth.welcome_back': 'Willkommen zurück',
  'dashboard.greeting': 'Willkommen zurück, {{name}}',
  'dashboard.assigned_simulations': 'Zugewiesene Simulationen',
  'dashboard.start_simulation': 'Simulation starten',
  'briefing.title': 'Szenario-Briefing',
  'briefing.start_simulation_btn': 'Simulationsraum betreten',
  'simulation.finish_session': 'Sitzung beenden',
  'simulation.retry': 'Simulation wiederholen',
  'scorecard.title': 'Bewertungs-Scorecard',
  'scorecard.overall_score': 'Gesamtergebnis',
  'breadcrumb.home': 'Startseite',
  'breadcrumb.admin': 'Administration',
  'breadcrumb.learner_portal': 'Lernendenportal',
  'breadcrumb.administration_console': 'Verwaltungskonsole',
  'breadcrumb.scenarios': 'Szenarien',
  'breadcrumb.my_attempts': 'Meine Versuche',
  'table.simulation': 'Simulation',
  'table.category': 'Kategorie',
  'table.status': 'Status',
  'table.actions': 'Aktionen',
  'table.member': 'Mitglied',
  'table.duration': 'Dauer',
  'table.score': 'Ergebnis',
  'table.view_details': 'Details anzeigen',
  'table.filter_all': 'Alle',
  'table.sort_latest': 'Sortieren: Neueste',
  'common.save': 'Speichern',
  'common.cancel': 'Abbrechen',
  'common.close': 'Schließen',
};

const ar: Record<string, string> = {
  ...en,
  'nav.dashboard': 'لوحة القيادة',
  'nav.simulations': 'المحاكاة',
  'nav.scenarios': 'السيناريوهات',
  'nav.courses': 'الدورات التدريبية',
  'nav.my_attempts': 'محاولاتي',
  'nav.assessments': 'التقييمات',
  'nav.reports': 'التقارير',
  'nav.settings': 'الإعدادات',
  'nav.admin_console': 'لوحة التحكم الإدارية',
  'nav.group_workspace': 'مساحة العمل',
  'nav.group_administration': 'الإدارة',
  'nav.sign_out': 'تسجيل الخروج',
  'nav.language': 'اللغة',
  'auth.sign_in': 'تسجيل الدخول',
  'auth.welcome_back': 'مرحبًا بعودتك',
  'dashboard.greeting': 'مرحبًا بعودتك، {{name}}',
  'dashboard.assigned_simulations': 'المحاكاة المعينة',
  'dashboard.start_simulation': 'بدء المحاكاة',
  'briefing.title': 'موجز السيناريو',
  'briefing.start_simulation_btn': 'الدخول إلى غرفة المحاكاة',
  'simulation.finish_session': 'إنهاء الجلسة',
  'simulation.retry': 'إعادة المحاولة',
  'scorecard.title': 'بطاقة نتائج التقييم',
  'scorecard.overall_score': 'الدرجة الإجمالية للأداء',
  'breadcrumb.home': 'الرئيسية',
  'breadcrumb.admin': 'الإدارة',
  'breadcrumb.learner_portal': 'بوابة المتدرب',
  'breadcrumb.administration_console': 'لوحة التحكم الإدارية',
  'breadcrumb.scenarios': 'السيناريوهات',
  'breadcrumb.my_attempts': 'محاولاتي',
  'table.simulation': 'المحاكاة',
  'table.category': 'الفئة',
  'table.status': 'الحالة',
  'table.actions': 'الإجراءات',
  'table.member': 'العضو',
  'table.duration': 'المدة',
  'table.score': 'الدرجة',
  'table.view_details': 'عرض التفاصيل',
  'table.filter_all': 'الكل',
  'table.sort_latest': 'ترتيب: الأحدث',
  'common.save': 'حفظ',
  'common.cancel': 'إلغاء',
  'common.close': 'إغلاق',
};

const ta: Record<string, string> = {
  ...en,
  'nav.dashboard': 'டாஷ்போர்டு',
  'nav.simulations': 'உருவகப்படுத்துதல்கள்',
  'nav.scenarios': 'சூழ்நிலைகள்',
  'nav.courses': 'படிப்புகள்',
  'nav.my_attempts': 'எனது முயற்சிகள்',
  'nav.assessments': 'மதிப்பீடுகள்',
  'nav.reports': 'அறிக்கைகள்',
  'nav.settings': 'அமைப்புகள்',
  'nav.admin_console': 'நிர்வாக பணியகம்',
  'nav.group_workspace': 'பணியிடம்',
  'nav.group_administration': 'நிர்வாகம்',
  'nav.sign_out': 'வெளியேறு',
  'nav.language': 'மொழி',
  'auth.sign_in': 'உள்நுழையவும்',
  'dashboard.greeting': 'மீண்டும் வருக, {{name}}',
  'dashboard.assigned_simulations': 'ஒதுக்கப்பட்ட உருவகப்படுத்துதல்கள்',
  'dashboard.start_simulation': 'தொடக்கவும்',
  'briefing.title': 'சூழ்நிலை சுருக்கம்',
  'briefing.start_simulation_btn': 'உருவகப்படுத்துதல் அறைக்குச் செல்க',
  'simulation.finish_session': 'அமர்வை முடிக்கவும்',
  'simulation.retry': 'மீண்டும் முயற்சிக்கவும்',
  'scorecard.title': 'மதிப்பீட்டு அறிக்கை',
  'scorecard.overall_score': 'மொத்த செயல்திறன் மதிப்பெண்',
  'breadcrumb.home': 'முகப்பு',
  'breadcrumb.admin': 'நிர்வாகம்',
  'breadcrumb.learner_portal': 'கற்பவர் போர்டல்',
  'breadcrumb.administration_console': 'நிர்வாக பணியகம்',
  'breadcrumb.scenarios': 'சூழ்நிலைகள்',
  'breadcrumb.my_attempts': 'எனது முயற்சிகள்',
  'table.simulation': 'உருவகப்படுத்துதல்',
  'table.category': 'வகை',
  'table.status': 'நிலை',
  'table.actions': 'செயல்கள்',
  'table.member': 'உறுப்பினர்',
  'table.duration': 'கால அளவு',
  'table.score': 'மதிப்பெண்',
  'table.view_details': 'விவரங்களை காண்க',
  'table.filter_all': 'அனைத்தும்',
  'table.sort_latest': 'வரிசைப்படுத்து: சமீபத்தியவை',
  'common.save': 'சேமிக்க',
  'common.cancel': 'ரத்துசெய்',
  'common.close': 'மூடு',
};

const te: Record<string, string> = {
  ...en,
  'nav.dashboard': 'డాష్‌బోర్డ్',
  'nav.simulations': 'సిమ్యులేషన్లు',
  'nav.scenarios': 'సన్నివేశాలు',
  'nav.courses': 'కోర్సులు',
  'nav.my_attempts': 'నా ప్రయత్నాలు',
  'nav.assessments': 'అంచనాలు',
  'nav.reports': 'నివేదికలు',
  'nav.settings': 'సెట్టింగ్‌లు',
  'nav.admin_console': 'పరిపాలనా కన్సోల్',
  'nav.group_workspace': 'వర్క్‌స్పేస్',
  'nav.group_administration': 'పరిపాలన',
  'nav.sign_out': 'లాగ్ అవుట్',
  'nav.language': 'భాష',
  'auth.sign_in': 'సైన్ ఇన్ చేయండి',
  'dashboard.greeting': 'తిరిగి స్వాగతం, {{name}}',
  'dashboard.assigned_simulations': 'కేటాయించిన సిమ్యులేషన్లు',
  'dashboard.start_simulation': 'ప్రారంభించండి',
  'briefing.title': 'సన్నివేశ వివరణ',
  'briefing.start_simulation_btn': 'సిమ్యులేషన్ గదిలోకి ప్రవేశించండి',
  'simulation.finish_session': 'ముగించండి',
  'simulation.retry': 'మళ్లీ ప్రయత్నించండి',
  'scorecard.title': 'మూల్యాంకన స్కోర్‌కార్డ్',
  'scorecard.overall_score': 'మొత్తం పనితీరు స్కోరు',
  'breadcrumb.home': 'హోమ్',
  'breadcrumb.admin': 'అడ్మిన్',
  'breadcrumb.learner_portal': 'అభ్యర్థి పోర్టల్',
  'breadcrumb.administration_console': 'పరిపాలనా కన్సోల్',
  'breadcrumb.scenarios': 'సన్నివేశాలు',
  'breadcrumb.my_attempts': 'నా ప్రయత్నాలు',
  'table.simulation': 'సిమ్యులేషన్',
  'table.category': 'వర్గం',
  'table.status': 'స్థితి',
  'table.actions': 'చర్యలు',
  'table.member': 'సభ్యుడు',
  'table.duration': 'వ్యవధి',
  'table.score': 'స్కోరు',
  'table.view_details': 'వివరాలు చూడండి',
  'table.filter_all': 'అన్నీ',
  'table.sort_latest': 'క్రమబద్ధీకరించు: తాజాది',
  'common.save': 'సేవ్ చేయండి',
  'common.cancel': 'రద్దు చేయండి',
  'common.close': 'మూసివేయండి',
};

const kn: Record<string, string> = {
  ...en,
  'nav.dashboard': 'ಡ್ಯಾಶ್‌ಬೋರ್ಡ್',
  'nav.simulations': 'ಸಿಮ್ಯುಲೇಶನ್‌ಗಳು',
  'nav.scenarios': 'ಸನ್ನಿವೇಶಗಳು',
  'nav.courses': 'ಕೋರ್ಸ್‌ಗಳು',
  'nav.my_attempts': 'ನನ್ನ ಪ್ರಯತ್ನಗಳು',
  'nav.assessments': 'ಮೌಲ್ಯಮಾಪನಗಳು',
  'nav.reports': 'ವರದಿಗಳು',
  'nav.settings': 'ಸೆಟ್ಟಿಂಗ್‌ಗಳು',
  'nav.admin_console': 'ಆಡಳಿತ ಕನ್ಸೋಲ್',
  'nav.group_workspace': 'ಕಾರ್ಯಕ್ಷೇತ್ರ',
  'nav.group_administration': 'ಆಡಳಿತ',
  'nav.sign_out': 'ಸೈನ್ ಔಟ್',
  'nav.language': 'ಭಾಷೆ',
  'auth.sign_in': 'ಸೈನ್ ಇನ್ ಮಾಡಿ',
  'dashboard.greeting': 'ಮರಳಿ ಸ್ವಾಗತ, {{name}}',
  'dashboard.assigned_simulations': 'ನಿಯೋಜಿತ ಸಿಮ್ಯುಲೇಶನ್‌ಗಳು',
  'dashboard.start_simulation': 'ಪ್ರಾರಂಭಿಸಿ',
  'briefing.title': 'ಸನ್ನಿವೇಶದ ವಿವರ',
  'briefing.start_simulation_btn': 'ಸಿಮ್ಯುಲೇಶನ್ ಕೋಣೆಗೆ ಪ್ರವೇಶಿಸಿ',
  'simulation.finish_session': 'ಮುಕ್ತಾಯಗೊಳಿಸಿ',
  'simulation.retry': 'ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ',
  'scorecard.title': 'ಮೌಲ್ಯಮಾಪನ ಸ್ಕೋರ್‌ಕಾರ್ಡ್',
  'scorecard.overall_score': 'ಒಟ್ಟು ಕಾರ್ಯಕ್ಷಮತೆ ಸ್ಕೋರ್',
  'breadcrumb.home': 'ಮುಖಪುಟ',
  'breadcrumb.admin': 'ಆಡಳಿತ',
  'breadcrumb.learner_portal': 'ಕಲಿಯುವವರ ಪೋರ್ಟಲ್',
  'breadcrumb.administration_console': 'ಆಡಳಿತ ಕನ್ಸೋಲ್',
  'breadcrumb.scenarios': 'ಸನ್ನಿವೇಶಗಳು',
  'breadcrumb.my_attempts': 'ನನ್ನ ಪ್ರಯತ್ನಗಳು',
  'table.simulation': 'ಸಿಮ್ಯುಲೇಶನ್',
  'table.category': 'ವರ್ಗ',
  'table.status': 'ಸ್ಥಿತಿ',
  'table.actions': 'ಕ್ರಮಗಳು',
  'table.member': 'ಸದಸ್ಯ',
  'table.duration': 'ಅವಧಿ',
  'table.score': 'ಅಂಕಗಳು',
  'table.view_details': 'ವಿವರಗಳನ್ನು ನೋಡಿ',
  'table.filter_all': 'ಎಲ್ಲವೂ',
  'table.sort_latest': 'ವಿಂಗಡಿಸಿ: ಇತ್ತೀಚಿನದು',
  'common.save': 'ಉಳಿಸಿ',
  'common.cancel': 'ರದ್ದುಮಾಡಿ',
  'common.close': 'ಮುಚ್ಚಿ',
};

const mr: Record<string, string> = {
  ...en,
  'nav.dashboard': 'डॅशबोर्ड',
  'nav.simulations': 'सिम्युलेशन',
  'nav.scenarios': 'परिदृश्य',
  'nav.courses': 'अभ्यासक्रम',
  'nav.my_attempts': 'माझे प्रयत्न',
  'nav.assessments': 'मूल्यमापन',
  'nav.reports': 'अहवाल',
  'nav.settings': 'सेटिंग्ज',
  'nav.admin_console': 'प्रशासन कन्सोल',
  'nav.group_workspace': 'कार्यक्षेत्र',
  'nav.group_administration': 'प्रशासन',
  'nav.sign_out': 'साइन आउट',
  'nav.language': 'भाषा',
  'auth.sign_in': 'साइन इन करा',
  'dashboard.greeting': 'पुन्हा स्वागत आहे, {{name}}',
  'dashboard.assigned_simulations': 'नियुक्त सिम्युलेशन',
  'dashboard.start_simulation': 'सुरू करा',
  'briefing.title': 'परिदृश्य माहिती',
  'briefing.start_simulation_btn': 'सिम्युलेशन रूममध्ये प्रवेश करा',
  'simulation.finish_session': 'सत्र पूर्ण करा',
  'simulation.retry': 'पुन्हा प्रयत्न करा',
  'scorecard.title': 'मूल्यमापन स्कोअरकार्ड',
  'scorecard.overall_score': 'एकूण कामगिरी गुण',
  'breadcrumb.home': 'मुख्यपृष्ठ',
  'breadcrumb.admin': 'प्रशासन',
  'breadcrumb.learner_portal': 'शिकणाऱ्यांचे पोर्टल',
  'breadcrumb.administration_console': 'प्रशासन कन्सोल',
  'breadcrumb.scenarios': 'परिदृश्य',
  'breadcrumb.my_attempts': 'माझे प्रयत्न',
  'table.simulation': 'सिम्युलेशन',
  'table.category': 'श्रेणी',
  'table.status': 'स्थिती',
  'table.actions': 'कृती',
  'table.member': 'सदस्य',
  'table.duration': 'कालावधी',
  'table.score': 'गुण',
  'table.view_details': 'तपशील पहा',
  'table.filter_all': 'सर्व',
  'table.sort_latest': 'क्रमवारी: नवीनतम',
  'common.save': 'जतन करा',
  'common.cancel': 'रद्द करा',
  'common.close': 'बंद करा',
};

const bn: Record<string, string> = {
  ...en,
  'nav.dashboard': 'ড্যাশবোর্ড',
  'nav.simulations': 'সিমুলেশন',
  'nav.scenarios': 'পরিস্থিতি',
  'nav.courses': 'কোর্সসমূহ',
  'nav.my_attempts': 'আমার প্রচেষ্টা',
  'nav.assessments': 'মূল্যায়ন',
  'nav.reports': 'প্রতিবেদন',
  'nav.settings': 'সেটিংস',
  'nav.admin_console': 'প্রশাসন কনসোল',
  'nav.group_workspace': 'কর্মক্ষেত্র',
  'nav.group_administration': 'প্রশাসন',
  'nav.sign_out': 'সাইন আউট',
  'nav.language': 'ভাষা',
  'auth.sign_in': 'সাইন ইন করুন',
  'dashboard.greeting': 'স্বাগতম, {{name}}',
  'dashboard.assigned_simulations': 'বরাদ্দকৃত সিমুলেশন',
  'dashboard.start_simulation': 'শুরু করুন',
  'briefing.title': 'পরিস্থিতির বিবরণ',
  'briefing.start_simulation_btn': 'সিমুলেশন রুমে প্রবেশ করুন',
  'simulation.finish_session': 'সমাপ্ত করুন',
  'simulation.retry': 'আবার চেষ্টা করুন',
  'scorecard.title': 'মূল্যায়ন স্কোরকার্ড',
  'scorecard.overall_score': 'সামগ্রিক স্কোর',
  'breadcrumb.home': 'হোম',
  'breadcrumb.admin': 'প্রশাসন',
  'breadcrumb.learner_portal': 'লার্নার পোর্টাল',
  'breadcrumb.administration_console': 'প্রশাসন কনসোল',
  'breadcrumb.scenarios': 'পরিস্থিতি',
  'breadcrumb.my_attempts': 'আমার প্রচেষ্টা',
  'table.simulation': 'সিমুলেশন',
  'table.category': 'বিভাগ',
  'table.status': 'অবস্থা',
  'table.actions': 'পদক্ষেপ',
  'table.member': 'সদস্য',
  'table.duration': 'সময়কাল',
  'table.score': 'স্কোর',
  'table.view_details': 'বিস্তারিত দেখুন',
  'table.filter_all': 'সব',
  'table.sort_latest': 'সাজান: সর্বশেষ',
  'common.save': 'সংরক্ষণ করুন',
  'common.cancel': 'বাতিল করুন',
  'common.close': 'বন্ধ করুন',
};

const pt: Record<string, string> = {
  ...en,
  'nav.dashboard': 'Painel',
  'nav.simulations': 'Simulações',
  'nav.scenarios': 'Cenários',
  'nav.courses': 'Cursos',
  'nav.my_attempts': 'Minhas Tentativas',
  'nav.assessments': 'Avaliações',
  'nav.reports': 'Relatórios',
  'nav.settings': 'Configurações',
  'nav.admin_console': 'Console de Administração',
  'nav.group_workspace': 'Espaço de Trabalho',
  'nav.group_administration': 'Administração',
  'nav.sign_out': 'Sair',
  'nav.language': 'Idioma',
  'auth.sign_in': 'Entrar',
  'auth.welcome_back': 'Bem-vindo de volta',
  'dashboard.greeting': 'Bem-vindo de volta, {{name}}',
  'dashboard.assigned_simulations': 'Simulações Atribuídas',
  'dashboard.start_simulation': 'Iniciar Simulação',
  'briefing.title': 'Instruções do Cenário',
  'briefing.start_simulation_btn': 'Entrar na Sala de Simulação',
  'simulation.finish_session': 'Concluir Sessão',
  'simulation.retry': 'Repetir Simulação',
  'scorecard.title': 'Cartão de Avaliação',
  'scorecard.overall_score': 'Pontuação Geral',
  'breadcrumb.home': 'Início',
  'breadcrumb.admin': 'Administração',
  'breadcrumb.learner_portal': 'Portal do Aluno',
  'breadcrumb.administration_console': 'Console de Administração',
  'breadcrumb.scenarios': 'Cenários',
  'breadcrumb.my_attempts': 'Minhas Tentativas',
  'table.simulation': 'Simulação',
  'table.category': 'Categoria',
  'table.status': 'Status',
  'table.actions': 'Ações',
  'table.member': 'Membro',
  'table.duration': 'Duração',
  'table.score': 'Pontuação',
  'table.view_details': 'Ver Detalhes',
  'table.filter_all': 'Todos',
  'table.sort_latest': 'Ordenar: Mais recente',
  'common.save': 'Salvar',
  'common.cancel': 'Cancelar',
  'common.close': 'Fechar',
};

const ja: Record<string, string> = {
  ...en,
  'nav.dashboard': 'ダッシュボード',
  'nav.simulations': 'シミュレーション',
  'nav.scenarios': 'シナリオ',
  'nav.courses': 'コース',
  'nav.my_attempts': '試行履歴',
  'nav.assessments': '評価',
  'nav.reports': 'レポート',
  'nav.settings': '設定',
  'nav.admin_console': '管理コンソール',
  'nav.group_workspace': 'ワークスペース',
  'nav.group_administration': '管理',
  'nav.sign_out': 'サインアウト',
  'nav.language': '言語',
  'auth.sign_in': 'サインイン',
  'auth.welcome_back': 'おかえりなさい',
  'dashboard.greeting': 'おかえりなさい、{{name}}さん',
  'dashboard.assigned_simulations': '割り当てられたシミュレーション',
  'dashboard.start_simulation': 'シミュレーションを開始',
  'briefing.title': 'シナリオ概要',
  'briefing.start_simulation_btn': 'シミュレーションルームへ入室',
  'simulation.finish_session': 'セッションを終了',
  'simulation.retry': 'もう一度やり直す',
  'scorecard.title': '評価スコアカード',
  'scorecard.overall_score': '総合スコア',
  'breadcrumb.home': 'ホーム',
  'breadcrumb.admin': '管理',
  'breadcrumb.learner_portal': '受講者ポータル',
  'breadcrumb.administration_console': '管理コンソール',
  'breadcrumb.scenarios': 'シナリオ',
  'breadcrumb.my_attempts': '試行履歴',
  'table.simulation': 'シミュレーション',
  'table.category': 'カテゴリー',
  'table.status': 'ステータス',
  'table.actions': 'アクション',
  'table.member': 'メンバー',
  'table.duration': '所要時間',
  'table.score': 'スコア',
  'table.view_details': '詳細を表示',
  'table.filter_all': 'すべて',
  'table.sort_latest': '並び順: 最新',
  'common.save': '保存',
  'common.cancel': 'キャンセル',
  'common.close': '閉じる',
};

const zh: Record<string, string> = {
  ...en,
  'nav.dashboard': '仪表板',
  'nav.simulations': '模拟练习',
  'nav.scenarios': '实战场景',
  'nav.courses': '培训课程',
  'nav.my_attempts': '我的练习记录',
  'nav.assessments': '能力评估',
  'nav.reports': '评估报告',
  'nav.settings': '系统设置',
  'nav.admin_console': '管理控制台',
  'nav.group_workspace': '工作区',
  'nav.group_administration': '系统管理',
  'nav.sign_out': '退出登录',
  'nav.language': '显示语言',
  'auth.sign_in': '登录',
  'auth.welcome_back': '欢迎回来',
  'dashboard.greeting': '欢迎回来，{{name}}',
  'dashboard.assigned_simulations': '已分配模拟练习',
  'dashboard.start_simulation': '开始模拟',
  'briefing.title': '演练情境简报',
  'briefing.start_simulation_btn': '进入模拟演练室',
  'simulation.finish_session': '完成演练',
  'simulation.retry': '重新演练',
  'scorecard.title': '评估能力成绩单',
  'scorecard.overall_score': '综合表现评分',
  'breadcrumb.home': '首页',
  'breadcrumb.admin': '管理',
  'breadcrumb.learner_portal': '学员门户',
  'breadcrumb.administration_console': '管理控制台',
  'breadcrumb.scenarios': '实战场景',
  'breadcrumb.my_attempts': '练习记录',
  'table.simulation': '模拟演练',
  'table.category': '分类',
  'table.status': '状态',
  'table.actions': '操作',
  'table.member': '成员',
  'table.duration': '时长',
  'table.score': '得分',
  'table.view_details': '查看详情',
  'table.filter_all': '全部',
  'table.sort_latest': '排序: 最新',
  'common.save': '保存',
  'common.cancel': '取消',
  'common.close': '关闭',
};

export const LOCALES: Record<string, Record<string, string>> = {
  en,
  'en-GB': enGB,
  'en-gb': enGB,
  'en_GB': enGB,
  es,
  hi,
  fr,
  de,
  ar,
  ta,
  te,
  kn,
  mr,
  bn,
  pt,
  ja,
  zh,
};
